/**
 * What a department has spent, and what it has promised to.
 *
 * "How much did Thane spend last quarter" has four honest answers, and giving
 * one of them is misleading. They are shown side by side on purpose: a budget
 * is overspent long before the ledger says so, and a department head who only
 * sees the posted figure finds out a month late.
 *
 * Below it, the same idea forward: what has to leave the bank week by week,
 * built from approved payments, commitment periods and loan instalments that
 * already exist. Nothing here is a trend line.
 */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api, formatInr } from '../api'
import { Card, Empty, ErrorNote, Loading, Note } from '../components/ui'

type Dimension = 'department' | 'location' | 'cost_centre' | 'category'

const DIMENSIONS: [Dimension, string][] = [
  ['department', 'Department'],
  ['location', 'Clinic'],
  ['cost_centre', 'Cost centre'],
  ['category', 'Category'],
]

interface Row {
  key: string | null
  name: string
  in_approval_paise: number
  committed_paise: number
  paid_unmatched_paise: number
  actual_paise: number
  in_approval_count: number
  committed_count: number
  total_promised_paise: number
}

interface PipelineData {
  dimension: string
  from_date: string
  to_date: string
  rows: Row[]
  totals: {
    in_approval_paise: number
    committed_paise: number
    paid_unmatched_paise: number
    actual_paise: number
  }
  note: string
}

interface ForecastItem {
  what: string
  due_on: string
  amount_paise: number
  outgoing: boolean
}

interface Week {
  week_of: string
  out_paise: number
  in_paise: number
  net_paise: number
  cumulative_paise: number
  items: ForecastItem[]
}

interface Forecast {
  from: string
  weeks: Week[]
  out_total_paise: number
  in_total_paise: number
  net_paise: number
  note: string
}

function financialYearStart(): string {
  const now = new Date()
  const year = now.getMonth() + 1 >= 4 ? now.getFullYear() : now.getFullYear() - 1
  return `${year}-04-01`
}

function Weeks() {
  const [open, setOpen] = useState<string | null>(null)
  const { data, isLoading, error } = useQuery({
    queryKey: ['cash-forecast'],
    queryFn: () => api.get<Forecast>('/reports/cash-forecast?weeks=12'),
  })
  if (isLoading) return <Loading what="Looking ahead" />
  if (error) return <ErrorNote error={error} />
  if (!data || data.weeks.length === 0)
    return <Empty>Nothing is scheduled to move in the next twelve weeks.</Empty>
  return (
    <>
      <table>
        <thead>
          <tr>
            <th>Week of</th>
            <th className="num">Out</th>
            <th className="num">In</th>
            <th className="num">Net</th>
            <th className="num">Running</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {data.weeks.map((w) => (
            <tr key={w.week_of}>
              <td className="muted date">{w.week_of}</td>
              <td className="num">{formatInr(w.out_paise)}</td>
              <td className="num">{formatInr(w.in_paise)}</td>
              <td className="num">{formatInr(w.net_paise)}</td>
              <td className="num muted">{formatInr(w.cumulative_paise)}</td>
              <td className="num">
                <button
                  className="small"
                  onClick={() => setOpen(open === w.week_of ? null : w.week_of)}
                >
                  {open === w.week_of ? 'Hide' : `${w.items.length} items`}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {open && (
        <table style={{ marginTop: 12 }}>
          <thead>
            <tr>
              <th>Due</th>
              <th>What</th>
              <th className="num">Amount</th>
            </tr>
          </thead>
          <tbody>
            {(data.weeks.find((w) => w.week_of === open)?.items ?? []).map((item, index) => (
              <tr key={`${item.what}-${index}`}>
                <td className="muted date">{item.due_on}</td>
                <td>
                  {item.what}
                  <span className="faint"> · {item.outgoing ? 'to pay' : 'to receive'}</span>
                </td>
                <td className="num">{formatInr(item.amount_paise)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <Note kind="info">{data.note}</Note>
    </>
  )
}

export default function Pipeline() {
  const [dimension, setDimension] = useState<Dimension>('department')
  const [from, setFrom] = useState(financialYearStart())

  const { data, isLoading, error } = useQuery({
    queryKey: ['pipeline', dimension, from],
    queryFn: () => api.get<PipelineData>(`/reports/pipeline?dimension=${dimension}&from_date=${from}`),
  })

  return (
    <>
      <Card
        title="Asked for, committed, paid, posted"
        hint="four answers to one question, kept apart"
        actions={
          <div style={{ display: 'flex', gap: 8 }}>
            <select
              value={dimension}
              onChange={(e) => setDimension(e.target.value as Dimension)}
              style={{ width: 'auto' }}
            >
              {DIMENSIONS.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
        }
        tight
      >
        <ErrorNote error={error} />
        {isLoading && <Loading what="Adding it up" />}
        {data && data.rows.length === 0 && <Empty>Nothing has been asked for in this period.</Empty>}
        {data && data.rows.length > 0 && (
          <table>
            <thead>
              <tr>
                <th>{DIMENSIONS.find(([v]) => v === dimension)?.[1]}</th>
                <th className="num">In approval</th>
                <th className="num">Committed</th>
                <th className="num">Paid, unmatched</th>
                <th className="num">In the ledger</th>
                <th className="num">All of it</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((row) => (
                <tr key={row.key ?? 'unassigned'}>
                  <td>{row.name}</td>
                  <td className="num muted">
                    {formatInr(row.in_approval_paise)}
                    {row.in_approval_count > 0 && (
                      <div className="faint" style={{ fontSize: 12 }}>
                        {row.in_approval_count} request(s)
                      </div>
                    )}
                  </td>
                  <td className="num">{formatInr(row.committed_paise)}</td>
                  <td className="num muted">{formatInr(row.paid_unmatched_paise)}</td>
                  <td className="num">{formatInr(row.actual_paise)}</td>
                  <td className="num">{formatInr(row.total_promised_paise)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <th>Everything</th>
                <th className="num">{formatInr(data.totals.in_approval_paise)}</th>
                <th className="num">{formatInr(data.totals.committed_paise)}</th>
                <th className="num">{formatInr(data.totals.paid_unmatched_paise)}</th>
                <th className="num">{formatInr(data.totals.actual_paise)}</th>
                <th />
              </tr>
            </tfoot>
          </table>
        )}
        {data && <Note kind="info">{data.note}</Note>}
      </Card>

      <Card title="What has to leave the bank" hint="the next twelve weeks" tight>
        <Weeks />
      </Card>
    </>
  )
}
